import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Theme, useMediaQuery } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  getReportDownloadLabel,
  reportDownloadDesktopSx,
} from "screens/Reports/helpers/reportsToolbarStyles";
import ReportsSearchField from "screens/Reports/components/ReportsSearchField";
import ReportsRecordList, {
  ReportRecord,
} from "screens/Reports/components/ReportsRecordList";
import { visuallyHidden } from "@mui/utils";
import moment from "moment";
import reportsTokens from "screens/Reports/Reports.tokens";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  reportsLoadingRowHeight,
  reportsTableCardSx,
  reportsTableIdentitySx,
  reportsTableNameSx,
  reportsTableSubSx,
  reportsTableSx,
} from "screens/Reports/ReportsTable.styles";
import { handleErrorMessage } from "helpers/methods";
import { SortingConfig } from "models/interfaces";
import {
  getTemplateEfficiencyCount,
  getTemplateEfficiencyPage,
} from "../services/ReportsService";

type TemplateRow = {
  rowId: string;
  template: JSX.Element;
  /** Names this row's selection checkbox for screen readers (WCAG 4.1.2). */
  selectionLabel: string;
  templateText: string;
  creatorName: string;
  timesUsed: number;
  completed: number;
  avgTurnaround: JSX.Element;
  /** Plain-text mirror of `avgTurnaround` for the CSV export. */
  avgTurnaroundText: string;
};

/**
 * The em dash the design uses for "no value" is punctuation most screen readers
 * skip, leaving the cell silent. The glyph is kept for sighted users and paired
 * with a text equivalent (WCAG 1.3.1).
 */
const NOT_AVAILABLE_TEXT = "—";
const NotAvailableCell = () => (
  <>
    <span aria-hidden="true">{NOT_AVAILABLE_TEXT}</span>
    <Box component="span" sx={visuallyHidden}>
      Not available
    </Box>
  </>
);

const HEADERS = [
  { name: "Template", field: "template", align: "left", sorting: false },
  { name: "Times Used", field: "timesUsed", align: "right", sorting: false },
  { name: "Completed", field: "completed", align: "right", sorting: false },
  {
    name: "Avg. Turnaround",
    field: "avgTurnaround",
    align: "right",
    sorting: false,
  },
];

/**
 * The report has no "all time" activity filter of its own — the analytics
 * tab draws no date-range control (see `Reports.dc.html`'s `TABLES.template`,
 * `dateRange:false`), so every template's lifetime totals are requested
 * instead of a rolling window.
 */
const ALL_TIME_RANGE = {
  fromDate: "2000-01-01",
  toDate: moment().format("YYYY-MM-DD"),
};

const DEFAULT_SORTING_CONFIG: SortingConfig[] = [
  { entity: "signRequest", field: "createdOn", order: "DESC" },
];

const toNumber = (val: any) => {
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const parsed = Number(val.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getAny = (obj: any, keys: string[], fallback: any = "") => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }
  return fallback;
};

const TemplatePerformanceReport = ({
  setLoader,
}: {
  setLoader?: (value: boolean) => void;
}) => {
  // Below `md` the reference swaps the table for cards — see
  // `ReportsRecordList`.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    page: 1,
    rowsPerPage: 10,
    searchText: "",
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, TemplateRow>
  >({});

  const convertTableData = (data: any[]): TemplateRow[] => {
    return data.map((item: any, index: number) => {
      const template = getAny(
        item,
        ["template_name", "templateName", "name", "template"],
        "-",
      );
      const creatorName = getAny(
        item,
        ["created_by_name", "createdByName", "owner_name", "ownerName", "name"],
        "-",
      );
      const timesUsed = toNumber(
        getAny(item, ["envelopes_sent", "envelopesSent", "sent"], 0),
      );
      const completed = toNumber(
        getAny(
          item,
          ["envelopes_completed", "envelopesCompleted", "completed"],
          0,
        ),
      );

      return {
        rowId: `${String(
          getAny(item, ["id", "template_id", "_id"], template),
        )}-${index}`,
        template: (
          <Box sx={reportsTableIdentitySx}>
            <Box sx={reportsTableNameSx} title={String(template)}>
              {String(template)}
            </Box>
            <Box sx={reportsTableSubSx}>Created by {creatorName}</Box>
          </Box>
        ),
        selectionLabel: `${String(template)}, created by ${String(creatorName)}`,
        templateText: String(template),
        creatorName: String(creatorName),
        timesUsed,
        completed,
        // Requires a per-template avg. sent-to-completed duration the backend
        // does not currently expose — see Backend Requests.
        avgTurnaround: <NotAvailableCell />,
        avgTurnaroundText: NOT_AVAILABLE_TEXT,
      };
    });
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setLoader?.(true);
      const payload = {
        dateRange: ALL_TIME_RANGE,
        searchText: filters.searchText,
        sortingConfig: DEFAULT_SORTING_CONFIG,
      };

      const countPayload = {
        dateRange: ALL_TIME_RANGE,
        searchText: filters.searchText,
      };

      const [dataResponse, countResponse] = await Promise.all([
        getTemplateEfficiencyPage(filters.page, payload),
        getTemplateEfficiencyCount(countPayload),
      ]);

      const rawRows = Array.isArray(dataResponse)
        ? dataResponse
        : Array.isArray(dataResponse?.data)
          ? dataResponse.data
          : Array.isArray(dataResponse?.content)
            ? dataResponse.content
            : [];

      setRows(convertTableData(rawRows));
      setCount(toNumber(countResponse?.count ?? countResponse ?? 0));
    } catch (error: any) {
      handleErrorMessage(error);
      setRows([]);
      setCount(0);
    } finally {
      setIsLoading(false);
      setLoader?.(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.rowsPerPage, filters.searchText]);

  useEffect(() => {
    setSelected([]);
    setSelectedRowsMap({});
  }, [filters.searchText]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setFilters((prev) => ({ ...prev, page: 1, searchText }));
  };

  const handlePageChange = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, page: 1, rowsPerPage }));
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    const foundRow = rows.find((r) => r.rowId === id);
    if (foundRow) {
      setSelectedRowsMap((prev) => ({ ...prev, [id]: foundRow }));
    }
  };

  const handleDeselectOne = (id: string) => {
    setSelected((prev) => prev.filter((item) => item !== id));
    setSelectedRowsMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleSelectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => {
      const next = [...prev];
      visibleRowIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.rowId] = row;
      });
      return next;
    });
  };

  const handleDeselectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      visibleRowIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const handleDownloadCSV = () => {
    const selectedData = selected
      .map((id) => selectedRowsMap[id])
      .filter(Boolean);

    const headers = [
      "Template",
      "Created By",
      "Times Used",
      "Completed",
      "Avg. Turnaround",
    ];

    const csvRows = selectedData.map((row) => [
      `"${row.templateText.replace(/"/g, '""')}"`,
      `"${row.creatorName.replace(/"/g, '""')}"`,
      row.timesUsed,
      row.completed,
      `"${row.avgTurnaroundText}"`,
    ]);

    const csvContent = [headers, ...csvRows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Template_Analytics_${moment().format("YYYY-MM-DD")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const records: ReportRecord[] = rows.map((row) => ({
    rowId: row.rowId,
    name: row.templateText,
    sub: row.creatorName === "-" ? undefined : `Created by ${row.creatorName}`,
    selectionLabel: row.selectionLabel,
    pairs: [
      { label: "Used", value: String(row.timesUsed) },
      { label: "Completed", value: String(row.completed) },
      { label: "Avg. Turnaround", value: row.avgTurnaroundText },
    ],
  }));

  return (
    <Stack
      spacing={{ xs: 1.75, sm: 2, md: reportsTokens.toolbar.marginBottom }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: reportsTokens.toolbar.gap }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <ReportsSearchField
          placeholder="Search templates"
          onSearchChange={handleSearchChange}
        />

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownloadCSV}
          disabled={selected.length === 0}
          sx={reportDownloadDesktopSx}
        >
          {getReportDownloadLabel(selected.length)}
        </Button>
      </Stack>

      {isCompact ? (
        <ReportsRecordList
          records={records}
          selected={selected}
          onSelectOne={handleSelectOne}
          onDeselectOne={handleDeselectOne}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isLoading={isLoading}
          onDownload={handleDownloadCSV}
          emptyTitle="No templates used yet"
          emptySub="Create a template and use it in a request to start tracking usage and turnaround."
          ariaLabel="Template analytics"
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          count={count}
          onPageChange={(nextPage) => handlePageChange(null, nextPage)}
        />
      ) : (
        <CustomTable
          emptyState={{
            icon: "template",
            title: "No templates used yet",
            body: "Send a request from a template and its usage is reported here.",
          }}
          loadingRowHeight={reportsLoadingRowHeight}
          cardSx={reportsTableCardSx}
          tableSxOverride={reportsTableSx}
          ariaLabel="Template analytics"
          headers={HEADERS}
          rows={rows}
          count={count}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          showJumpToPageNumber={false}
          handleOnChangePageInput={handlePageChange}
          showPagination
          isLoading={isLoading}
          showCheckbox={true}
          hideSelectedCount
          highlightSelectedRows
          selected={selected}
          onSelectOne={handleSelectOne}
          onSelectAll={handleSelectAll}
          onDeselectOne={handleDeselectOne}
          onDeselectAll={handleDeselectAll}
        />
      )}
    </Stack>
  );
};

export default TemplatePerformanceReport;
